import { IBookingQuoteRevisionLine } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';

/**
 * The visit, as the one line of a service quote that belongs to nobody's
 * editor.
 *
 * A professional who charges for going to look at a job (see
 * ServiceProviderProfile.ChargesVisitFee on the backend) gets that fee written
 * into every version of the quote by the server, from the figure snapshotted on
 * the request when the customer sent it. It is deliberately not theirs to edit:
 * the customer agreed to it before anybody travelled, and it is what they still
 * owe if they say no to everything else.
 *
 * This file exists because the front has to agree with that in two places at
 * once -- the composer for a request's quote and, later, the on-site revision --
 * and because both of them face the same trap: an editor that prefills from the
 * previous version will happily hand the visit back as an ordinary line, and a
 * professional who edits it there watches their change silently disappear (the
 * server drops what it did not write). So it comes out of the editable set and
 * is shown locked instead.
 */
export const VISIT_FEE_ORIGIN = 'Visit';

/** Is this the server's own visit line, rather than something somebody typed? */
export const isVisitFeeLine = (line: IBookingQuoteRevisionLine): boolean => line.origin === VISIT_FEE_ORIGIN;

/**
 * The locked rows to show above a service quote's editor, in the order a
 * professional should read them: the visit first, because it is already agreed.
 *
 * Two cases, and the second is the one worth the helper. On a revision the
 * saved version already carries the visit, so those lines are what is shown --
 * exactly what the customer has in front of them. On the FIRST quote there is
 * no saved version yet and the server has not written anything: the fee is only
 * a figure on the request. Left alone, the professional would compose a quote
 * whose total is 40 less than what the customer is about to be asked for, which
 * is the kind of surprise that makes somebody distrust the whole screen. So one
 * is synthesised from the request's own snapshot.
 *
 * `title` is passed in translated rather than built here -- this helper has no
 * business owning copy.
 */
export const visitFeeLockedLines = (
  savedLines: IBookingQuoteRevisionLine[] | undefined,
  visitFeeAmount: number | undefined,
  title: string,
): IBookingQuoteRevisionLine[] => {
  const saved = (savedLines ?? []).filter((line) => !line.isEditableByHost);
  if (saved.length > 0) return saved;
  if (!visitFeeAmount || visitFeeAmount <= 0) return [];
  return [{ title, amount: visitFeeAmount, origin: VISIT_FEE_ORIGIN, isEditableByHost: false }];
};

/** Whatever of a saved version the professional may actually reprice. */
export const editableQuoteLines = (
  savedLines: IBookingQuoteRevisionLine[] | undefined,
): IBookingQuoteRevisionLine[] => (savedLines ?? []).filter((line) => line.isEditableByHost);

/**
 * Why a locked line is locked, which is not the same sentence for all of them.
 *
 * The standing hint -- "somebody else charges this, you can't change it" -- is
 * true of platform insurance and of an independent provider's work, and flatly
 * wrong about the visit: that money is this professional's own. Getting it wrong
 * would read as the platform keeping their travel fee.
 */
export const lockedLineHintKey = (line: IBookingQuoteRevisionLine): string =>
  isVisitFeeLine(line) ? TRANSLATIONS.VISIT_FEE_LOCKED_HINT : TRANSLATIONS.QUOTE_EDITOR_LOCKED_HINT;
